import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ZagButton } from '../button/button';

/**
 * WidgetCard Component
 *
 * A flexible card container for dashboard widgets with optional header actions.
 * Used for charts, lists, and other dashboard content.
 *
 * @example
 * ```html
 * <zag-widget-card title="Today's Shifts" actionLabel="View All" (actionClick)="viewAll()">
 *   <!-- content -->
 * </zag-widget-card>
 * ```
 */
@Component({
  selector: 'zag-widget-card',
  imports: [ZagButton],
  template: `
    <div class="zag-widget-card__header">
      <h2 class="zag-widget-card__title">{{ title() }}</h2>
      @if (actionLabel()) {
        <button zagButton zagType="primary" class="zag-widget-card__action" (click)="actionClick.emit()">
          {{ actionLabel() }}
        </button>
      }
      <ng-content select="[slot=action]"></ng-content>
    </div>
    <div class="zag-widget-card__content">
      <ng-content></ng-content>
    </div>
    @if (showFooter()) {
      <div class="zag-widget-card__footer">
        <ng-content select="[slot=footer]"></ng-content>
      </div>
    }
  `,
  styleUrl: './widget-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-widget-card]': 'true',
  },
})
export class WidgetCard {
  /** Card title */
  readonly title = input.required<string>();

  /** Optional action button label */
  readonly actionLabel = input<string>('');

  /** Whether to show footer slot */
  readonly showFooter = input<boolean>(false);

  /** Emits when action button is clicked */
  readonly actionClick = output<void>();
}
